import { difference, isArray, isEqual } from 'lodash';
import React, { RefObject, memo, useEffect, useRef } from 'react';
import TagManager from 'react-gtm-module';
import { Props } from './withGTM.interface';

const mapContainerTarget = ({ target, selector }: { target: HTMLElement; selector: string }) => {
  const containerEl = target && ((target as HTMLElement).closest(selector) as HTMLElement);

  if (!containerEl) {
    return undefined;
  }

  return containerEl;
};

/**
 * Keep in mind that selectors should respect ORDER of priority.
 * @param selectors
 */
export const mapTargetBySelectors =
  (selectors: string[]) =>
  ({ target }: { target: HTMLElement }) => {
    const containerEl = selectors
      .map((selector) => mapContainerTarget({ target, selector }))
      .find((targetFound) => targetFound);

    return containerEl;
  };

export const mapAnchorTarget = ({ target }: { target: HTMLElement }) => {
  const closeTargetEl = target && ((target as HTMLElement).closest('a') as HTMLAnchorElement);
  const isAnchorEl = target instanceof HTMLAnchorElement;

  // Only available for anchor elements.
  if (!closeTargetEl && !isAnchorEl) {
    return undefined;
  }

  // Getting the anchor element.
  const anchorEl = isAnchorEl ? target : closeTargetEl;

  return anchorEl;
};

const defaultConfig = { eventName: 'click', isCustom: false, mapTarget: mapAnchorTarget };

export const withGTM = ({ Component, onDatalayer, config, style: overrideStyle = {} }: Props) =>
  memo((props: any) => {
    const componentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const configuration = { ...defaultConfig, ...config };
      const targetEl = componentRef?.current as HTMLElement;

      const pushDatalayer = ({ dataLayer }: any) => {
        // If there is no datalayer to push then it stops.
        if (!dataLayer) {
          return;
        }

        // Pushes the event.
        TagManager.dataLayer({ dataLayer });
      };

      const handleClick = (target: any) => {
        const targetMapped = configuration.mapTarget({ target });

        if (!targetMapped) {
          return;
        }

        // Sending ref and the anchor element (target).
        const dataLayer = onDatalayer({ componentRef, target: targetMapped });
        pushDatalayer({ dataLayer });
      };

      const handleCustomEvent = (content: any) => {
        if (!Object.keys(content).length) {
          return;
        }

        const dataLayer = onDatalayer({ componentRef, target: content });
        pushDatalayer({ dataLayer });
      };

      const handleEvent = ((e: CustomEvent) => {
        const { detail, target } = e;

        if (['click', 'gtm:click:link', 'gtm:click:cta'].includes(configuration.eventName)) {
          handleClick(configuration.isCustom ? detail.target : target);
        } else {
          handleCustomEvent(detail.target);
        }
      }) as EventListener;

      if (targetEl) {
        targetEl.addEventListener(configuration.eventName, handleEvent, { capture: true });
      }

      return () => {
        if (targetEl) {
          targetEl.removeEventListener(configuration.eventName, handleEvent, { capture: true });
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [componentRef]);

    /* eslint-disable jsx-a11y/no-static-element-interactions */
    /* eslint-disable jsx-a11y/click-events-have-key-events */
    return (
      <div className="gtm-container" style={{ width: '100%', ...overrideStyle }} ref={componentRef}>
        <Component {...props} />
      </div>
    );
  }, isEqual);

// To avoid multi component instances
export const withGTMPipe =
  (...gtmProps: Omit<Props, 'Component'>[]) =>
  (Component: any) =>
    gtmProps.reduce(
      (lastComponent, gtmProp) =>
        withGTM({
          Component: lastComponent,
          ...gtmProp,
        }),
      Component,
    );

const removeTaxPrefix = (text: string) => text?.replace(/^tax_/, '');

// Get query format for search components
export const getQueries = (args: any) => {
  if (!Object.keys(args).length) {
    return '';
  }

  const delimiter = '|';

  return Object.keys(args)
    .filter((key: string) => {
      const value = args[key];

      return !Array.isArray(value) ? true : !!value.length;
    })
    .map((key: string) => {
      const value = args[key];

      if (Array.isArray(value)) {
        return value.map((val: string) => `${removeTaxPrefix(key)}="${val}"`).join(delimiter);
      }

      return `${removeTaxPrefix(key)}="${value}"`;
    })
    .join(delimiter);
};

export const getDataLayerSearchFilters = (args: Record<string, string[] | string> = {}): string =>
  getQueries(args);

export const getDataLayerNumFilters = (args: Record<string, string[] | string> = {}): number =>
  Object.values(args)?.flat()?.length || 0;

export const getDataLayerSearchFiltersObject = (
  args: Record<string, string[] | string> = {},
): Record<string, string> => {
  return Object.keys(args)?.reduce((acc, curr) => {
    if (!args?.[curr] || (isArray(args?.[curr]) && !args?.[curr]?.length)) return { ...acc };

    return {
      ...acc,
      [`${removeTaxPrefix(curr)}_filter`]: isArray(args?.[curr])
        ? (args?.[curr] as string[])?.join('|')
        : args?.[curr],
    };
  }, {});
};

interface DataLayerByContentCategoryKeysProps {
  dataLayerSearchAttributesByContentCategory?: Record<string, string>;
  contentCategoryKeys?: string[];
}

export const getDataLayerByContentCategoryKeys = ({
  dataLayerSearchAttributesByContentCategory = {},
  contentCategoryKeys = [],
}: DataLayerByContentCategoryKeysProps) => {
  try {
    if (!contentCategoryKeys?.length) throw new Error('Content category keys are required.');

    const contentTypesLowerCase = contentCategoryKeys?.map((record) => record.toLowerCase());
    const SearchAttributesMapper: Array<[string[], Record<string, string>]> = Object.keys(
      dataLayerSearchAttributesByContentCategory,
    )?.map((key) => [
      key?.split(',')?.map((x) => x?.trim()),
      JSON.parse(dataLayerSearchAttributesByContentCategory?.[key]) as Record<string, string>,
    ]);

    const dataLayerAttributes = SearchAttributesMapper?.find(([keys]) => {
      return !difference(keys, contentTypesLowerCase)?.length;
    });
    if (!dataLayerAttributes?.length) throw new Error('Content category keys didn`t match.');

    return dataLayerAttributes?.[1];
  } catch (error) {
    return { event: '', event_name: '' };
  }
};

// PUSH for custom events.
export const datalayerPush = ({
  compRef,
  args,
  event = 'gtm:custom',
}: {
  compRef: RefObject<HTMLDivElement>;
  args: any;
  event?: string;
}) => {
  compRef?.current?.dispatchEvent(
    new CustomEvent(event, {
      bubbles: true,
      detail: { target: { ...args } },
    }),
  );
};

export const getPageNumber = ({ target, page }: { target: HTMLElement; page: number }) => {
  const isIcon = !!target?.querySelector('svg');

  if (isIcon) {
    const isNextPage =
      !!target?.querySelector('path[d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"]') ||
      !!target?.querySelector('path[d="M8.59 16.34l4.58-4.59-4.58-4.59L10 5.75l6 6-6 6z"]');
    const increment = isNextPage ? +1 : -1;

    return Number(page) + increment;
  }

  return Number(target?.textContent);
};
